import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trophy, UserPlus, Copy, Trash2, Send, KeyRound, Shield, Pencil } from "lucide-react";
import PermissionsModal from "@/components/PermissionsModal";
import EmpresaSettings from "@/components/EmpresaSettings";
import { toast } from "sonner";

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "vendedor", password: "", telefono: "" });
  const [tempPassword, setTempPassword] = useState(null);
  const [sending, setSending] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [permsTarget, setPermsTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", telefono: "", role: "vendedor" });

  const load = async () => {
    const [t, r] = await Promise.all([api.get("/team"), api.get("/analytics/vendedores")]);
    setMembers(t.data);
    setRanking(r.data.ranking || []);
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    // Regla: el teléfono es obligatorio para vendedores (aparece en sus PDFs).
    if (form.role === "vendedor" && !(form.telefono || "").trim()) {
      toast.error("El teléfono del vendedor es obligatorio. Aparece en los PDF de los pedidos que él genere.");
      return;
    }
    try {
      const { data } = await api.post("/team", form);
      setTempPassword({ email: data.email, password: data.temp_password });
      setForm({ name: "", email: "", role: "vendedor", password: "", telefono: "" });
      load();
      toast.success(`${data.name} agregado al equipo`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar");
    }
  };

  const removeMember = async (uid) => {
    const m = members.find((x) => x.id === uid);
    const nombre = m?.name || m?.email || "este integrante";
    const msg = `¿Eliminar a ${nombre} del equipo?\n\n` +
      `• Perderá el acceso al sistema.\n` +
      `• Sus pedidos y clientes NO se borran (quedan sin asignar).\n` +
      `• Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    await api.delete(`/team/${uid}`);
    load();
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPass.length < 4) { toast.error("Minimo 4 caracteres"); return; }
    try {
      const { data } = await api.post(`/team/${resetTarget.id}/reset-password`, { new_password: newPass });
      setTempPassword({ email: data.email, password: data.new_password });
      setResetTarget(null);
      setNewPass("");
      toast.success("Contrasena restablecida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restablecer");
    }
  };

  const sendReports = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/send-now");
      toast.success(`Enviados: ${data.sent} · Fallidos: ${data.failed}`);
    } catch (e) {
      toast.error("No se pudo enviar. Verifica RESEND_API_KEY.");
    } finally { setSending(false); }
  };

  const openEdit = (m) => {
    setEditForm({
      name: m.name || "",
      email: m.email || "",
      telefono: m.telefono || "",
      role: m.role || "vendedor",
    });
    setEditTarget(m);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    if (!editForm.name.trim()) { toast.error("El nombre no puede quedar vacío"); return; }
    if (!editForm.email.trim() || !/@/.test(editForm.email)) { toast.error("Correo inválido"); return; }
    if (editForm.role === "vendedor" && !editForm.telefono.trim()) {
      toast.error("El teléfono del vendedor es obligatorio (aparece en sus PDFs)");
      return;
    }
    try {
      await api.patch(`/team/${editTarget.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
        telefono: editForm.telefono.trim(),
        role: editForm.role,
      });
      toast.success(`${editForm.name} actualizado`);
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    }
  };

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Administración</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Equipo</h1>
          <p className="text-muted-foreground mt-2">Administra vendedores, administradores y envía reportes.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sendReports} disabled={sending} data-testid="send-reports-btn" className="h-11">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : "Enviar reporte ahora"}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="invite-btn" className="bg-primary hover:bg-primary/90 h-11">
            <UserPlus className="w-4 h-4 mr-2" /> Agregar integrante
          </Button>
        </div>
      </header>

      {/* Empresa settings */}
      <EmpresaSettings />

      {/* Ranking */}
      <div className="bg-card border border-border rounded-xl p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-amber-600" />
          <h3 className="font-display text-base font-semibold text-foreground">Ranking de vendedores — mes actual</h3>
        </div>
        {ranking.length === 0 && <div className="text-sm text-muted-foreground/70 py-4">Sin ventas del mes aún.</div>}
        <div className="divide-y divide-slate-100">
          {ranking.map((v, i) => (
            <div key={v.vendedor} className="py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-200 text-foreground/80" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-muted text-muted-foreground"}`}>
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{v.vendedor}</div>
                  <div className="text-xs text-muted-foreground">{v.pedidos} pedidos · {v.clientes} clientes</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-foreground tabular-nums">{formatCOP(v.total)}</div>
                {v.delta !== null && v.delta !== undefined && (
                  <div className={`text-xs font-medium ${v.delta >= 0 ? "text-primary" : "text-rose-600"}`}>
                    {v.delta >= 0 ? "↑" : "↓"} {Math.abs(v.delta).toFixed(0)}% vs mes anterior
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Members */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          <div className="col-span-3">Nombre</div>
          <div className="col-span-3">Correo</div>
          <div className="col-span-2">Teléfono</div>
          <div className="col-span-2">Perfil</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {members.map((m) => (
          <div key={m.id} className="flex flex-col md:grid md:grid-cols-12 gap-3 md:gap-4 px-4 md:px-6 py-4 border-b border-border/60 md:items-center">
            <div className="md:col-span-3 text-sm text-foreground font-semibold flex items-center justify-between md:justify-start">
              <span>{m.name}</span>
              <span className={`md:hidden text-[10px] px-2 py-0.5 rounded-full ${m.template === "director" ? "bg-primary text-primary-foreground" : m.template === "operador" ? "bg-blue-100 text-blue-800" : m.template === "personalizado" ? "bg-amber-100 text-amber-800" : "bg-muted text-foreground/80"}`}>
                {m.template ? m.template.charAt(0).toUpperCase() + m.template.slice(1) : (m.role === "admin" ? "Director" : m.role === "operador" ? "Operador" : "Vendedor")}
              </span>
            </div>
            <div className="md:col-span-3 text-xs md:text-sm text-muted-foreground truncate">{m.email}</div>
            <div className="md:col-span-2">
              <Input
                defaultValue={m.telefono || ""}
                placeholder="+57 300 000 0000"
                className="h-9 md:h-8 text-xs"
                data-testid={`tel-inline-${m.email}`}
                onBlur={async (e) => {
                  const v = e.target.value.trim();
                  if (v === (m.telefono || "")) return;
                  try {
                    await api.patch(`/team/${m.id}`, { telefono: v });
                    toast.success(`Teléfono de ${m.name} actualizado`);
                    load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "No se pudo guardar el teléfono");
                  }
                }}
              />
            </div>
            <div className="hidden md:block md:col-span-1">
              <span className={`text-xs px-2 py-1 rounded-full ${m.template === "director" ? "bg-primary text-primary-foreground" : m.template === "operador" ? "bg-blue-100 text-blue-800" : m.template === "personalizado" ? "bg-amber-100 text-amber-800" : "bg-muted text-foreground/80"}`}>
                {m.template ? m.template.charAt(0).toUpperCase() + m.template.slice(1) : (m.role === "admin" ? "Director" : m.role === "operador" ? "Operador" : "Vendedor")}
              </span>
            </div>
            <div className="md:col-span-2 flex flex-wrap justify-end gap-2 md:gap-1 pt-2 md:pt-0 border-t md:border-t-0 border-border/40">
              <button onClick={() => openEdit(m)} className="flex-1 basis-[calc(50%-4px)] md:flex-none md:basis-auto inline-flex items-center justify-center gap-1.5 h-10 md:h-9 md:w-9 px-2 md:px-0 rounded-md border border-border md:border-transparent hover:bg-muted text-muted-foreground/80 hover:text-foreground text-xs font-medium min-w-0" title="Editar integrante" data-testid={`edit-${m.email}`}>
                <Pencil className="w-4 h-4 flex-shrink-0" />
                <span className="md:hidden truncate">Editar</span>
              </button>
              <button onClick={() => setPermsTarget(m)} className="flex-1 basis-[calc(50%-4px)] md:flex-none md:basis-auto inline-flex items-center justify-center gap-1.5 h-10 md:h-9 md:w-9 px-2 md:px-0 rounded-md border border-border md:border-transparent hover:bg-pink-50 text-muted-foreground/80 hover:text-pink-600 text-xs font-medium min-w-0" title="Permisos" data-testid={`perms-${m.email}`}>
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span className="md:hidden truncate">Permisos</span>
              </button>
              <button onClick={() => setResetTarget(m)} className="flex-1 basis-[calc(50%-4px)] md:flex-none md:basis-auto inline-flex items-center justify-center gap-1.5 h-10 md:h-9 md:w-9 px-2 md:px-0 rounded-md border border-border md:border-transparent hover:bg-muted text-muted-foreground/80 hover:text-foreground text-xs font-medium min-w-0" title="Restablecer contraseña" data-testid={`reset-pw-${m.email}`}>
                <KeyRound className="w-4 h-4 flex-shrink-0" />
                <span className="md:hidden truncate">Clave</span>
              </button>
              <button onClick={() => removeMember(m.id)} className="flex-1 basis-[calc(50%-4px)] md:flex-none md:basis-auto inline-flex items-center justify-center gap-1.5 h-10 md:h-9 md:w-9 px-2 md:px-0 rounded-md border border-border md:border-transparent hover:bg-rose-50 text-muted-foreground/80 hover:text-rose-600 text-xs font-medium min-w-0" title="Eliminar">
                <Trash2 className="w-4 h-4 flex-shrink-0" />
                <span className="md:hidden truncate">Borrar</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Invite modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Agregar integrante</DialogTitle>
            <p className="text-sm text-muted-foreground">Le llegara una contrasena temporal.</p>
          </DialogHeader>
          <form onSubmit={invite} className="space-y-4 mt-4">
            <div>
              <Label>Nombre completo</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1" placeholder="Ej: Andrea Torres" />
            </div>
            <div>
              <Label>Correo</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" placeholder="correo@empresa.com" />
            </div>
            <div>
              <Label>Teléfono / WhatsApp del asesor {form.role === "vendedor" && <span className="text-rose-600">*</span>}</Label>
              <Input
                required={form.role === "vendedor"}
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="h-11 mt-1"
                placeholder="+57 300 000 0000"
                data-testid="invite-telefono"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {form.role === "vendedor"
                  ? "Obligatorio para vendedores. Aparecerá en los PDF de cotizaciones y remisiones que genere."
                  : "Aparecerá junto al nombre del asesor en los PDF de cotizaciones y remisiones."}
              </p>
            </div>
            <div>
              <Label>Rol</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["vendedor", "operador", "admin"].map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, role: r })} className={`p-3 rounded-lg border text-sm text-left ${form.role === r ? "border-slate-900 bg-muted/40" : "border-border"}`}>
                    <div className="font-semibold">{r === "admin" ? "Director" : r === "operador" ? "Operador" : "Vendedor"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{r === "admin" ? "Ve todo, administra el equipo" : r === "operador" ? "Bodega: inventario, despachos y pagos" : "Solo ve sus ventas y clientes"}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Contrasena (opcional)</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 mt-1" placeholder="Dejar vacio para usar 'beem2026'" />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 h-11" data-testid="invite-submit">Agregar</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Temp password modal */}
      <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Comparta estos datos</DialogTitle>
            <p className="text-sm text-muted-foreground">Envie esta informacion a la persona para que pueda ingresar.</p>
          </DialogHeader>
          {tempPassword && (
            <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted/40 border border-border rounded-lg">
                <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Correo</div>
                <div className="font-mono text-sm mt-1">{tempPassword.email}</div>
              </div>
              <div className="p-4 bg-muted/40 border border-border rounded-lg">
                <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Contrasena temporal</div>
                <div className="font-mono text-sm mt-1">{tempPassword.password}</div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(`Correo: ${tempPassword.email}\nContrasena: ${tempPassword.password}\nIngresa en: ${window.location.origin}`);
                  toast.success("Copiado al portapapeles");
                }}
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar todo
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password modal */}
      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Restablecer contrasena</DialogTitle>
            <p className="text-sm text-muted-foreground">Asigne una contrasena nueva para {resetTarget?.name}. Compartasela para que pueda ingresar.</p>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4 mt-4">
            <div>
              <Label>Nueva contrasena</Label>
              <Input data-testid="reset-pw-input" required value={newPass} onChange={(e) => setNewPass(e.target.value)} className="h-11 mt-1" placeholder="Minimo 4 caracteres" />
            </div>
            <Button type="submit" data-testid="reset-pw-submit" className="w-full bg-primary hover:bg-primary/90 h-11">Restablecer</Button>
          </form>
        </DialogContent>
      </Dialog>
      {/* Edit member modal */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent data-testid="edit-member-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Editar integrante</DialogTitle>
            <p className="text-sm text-muted-foreground">Actualiza los datos de {editTarget?.name}.</p>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-4">
            <div>
              <Label>Nombre completo</Label>
              <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-11 mt-1" data-testid="edit-name" />
            </div>
            <div>
              <Label>Correo</Label>
              <Input required type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="h-11 mt-1" data-testid="edit-email" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Al cambiar el correo, el integrante debe usarlo en el próximo login.</p>
            </div>
            <div>
              <Label>Teléfono / WhatsApp {editForm.role === "vendedor" && <span className="text-rose-600">*</span>}</Label>
              <Input value={editForm.telefono} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} className="h-11 mt-1" placeholder="+57 300 000 0000" data-testid="edit-telefono" />
              {editForm.role === "vendedor" && (
                <p className="text-[10px] text-muted-foreground mt-0.5">Obligatorio para vendedores (aparece en sus PDFs como contacto del asesor).</p>
              )}
            </div>
            <div>
              <Label>Rol</Label>
              <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm" data-testid="edit-role">
                <option value="vendedor">Vendedor</option>
                <option value="operador">Operador</option>
                <option value="admin">Director / Admin</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-0.5">Cambiar el rol modifica los permisos base. Si el integrante tenía permisos personalizados, se conservan.</p>
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 h-11" data-testid="edit-save">
              Guardar cambios
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions modal */}
      {permsTarget && <PermissionsModal member={permsTarget} onClose={(saved) => { setPermsTarget(null); if (saved) load(); }} />}
    </div>
  );
}
